import { HELP_TOPIC_LABELS, type HelpTopic } from '@sirocco/shared';
import { useState } from 'react';
import { support } from '../../api/endpoints';
import { SubScreen } from '../../components/layout/Screens';
import { Button } from '../../components/ui/Button';
import { TextArea } from '../../components/ui/Field';
import { t } from '../../i18n';

/** Ordre des motifs : du plus courant au moins courant. */
const TOPICS = Object.keys(HELP_TOPIC_LABELS) as HelpTopic[];

/**
 * Aide et signalements.
 *
 * Deux questions et rien d'autre : de quoi il s'agit, et quoi. Pas de
 * carte autour de chaque chose — un formulaire de trois lignes n'a pas
 * besoin d'etre range dans des blocs pour se lire.
 */
export function HelpScreen() {
  const [topic, setTopic] = useState<HelpTopic | null>(null);
  const [message, setMessage] = useState('');
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(null);

  async function send() {
    if (!topic) return;
    setPending(true);
    setFailure(null);
    try {
      const response = await support.send(topic, message);
      // Le formulaire se vide : on ne renvoie pas deux fois le meme texte
      // parce qu'on ne sait pas si le premier est parti.
      setSent(response.message);
      setTopic(null);
      setMessage('');
    } catch (cause) {
      setFailure(cause instanceof Error ? cause.message : t.profil.aide.impossible);
    } finally {
      setPending(false);
    }
  }

  return (
    <SubScreen
      title={t.profil.commun.aide}
      bottomBar={
        // Pas de bouton pleine largeur : "Envoyer" n'a pas besoin de
        // rejoindre les deux bords de l'ecran pour se faire trouver.
        <div className="pr-help__send">
          <Button
            loading={pending}
            disabled={topic === null || message.trim().length === 0}
            onClick={() => void send()}
          >
            {t.profil.aide.envoyer}
          </Button>
        </div>
      }
    >
      <p className="pr-intro">
        {t.profil.aide.intro}
      </p>

      <p className="pr-help__label">{t.profil.aide.sujet}</p>
      <div className="pr-help__topics">
        {TOPICS.map((value) => (
          <button
            key={value}
            className={`pr-help__topic${topic === value ? ' pr-help__topic--on' : ''}`}
            aria-pressed={topic === value}
            // Un second appui sur le meme motif le deselectionne.
            onClick={() => setTopic(topic === value ? null : value)}
          >
            {t.profil.aide.sujets[value]}
          </button>
        ))}
      </div>

      <TextArea
        tone="spot"
        label={t.profil.aide.raconte}
        className="pr-help__area"
        placeholder={t.profil.aide.raconteExemple}
        rows={7}
        maxLength={1200}
        value={message}
        onChange={(changed) => setMessage(changed.target.value)}
      />

      {failure && <p className="field__error">{failure}</p>}
      {sent && <p className="pr-help__done">{sent}</p>}
    </SubScreen>
  );
}
